import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, Zap, ArrowRight, Rocket, Plus, Star, Eye } from "lucide-react";
import { api } from "../lib/api";
import { TemplatePreview, TemplateThumb, TemplateCard } from "../components/TemplateCard";
import { useCart } from "../context/CartContext";
import { ShareButtons } from "../components/ShareButtons";

const funnelSteps = [
  { n: "1", t: "Preview & Pick", d: "Choose the template that fits your business and brand." },
  { n: "2", t: "Checkout in Minutes", d: "Secure Stripe checkout. First-timers get a free template." },
  { n: "3", t: "Launch & Grow", d: "Get your files instantly, then let us handle SEO & marketing." },
];

export default function TemplateDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const { add, has } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    setData(null);
    api.get(`/templates/${slug}`).then(({ data }) => {
      setData(data);
      document.title = data.template.seo_title;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = data.template.seo_description;
    }).catch(() => {});
    return () => { document.title = "San Antonio's Web Design"; };
  }, [slug]);

  if (!data) return <div className="mx-auto max-w-4xl px-6 py-24 text-center text-muted-foreground">Loading…</div>;
  const t = data.template;
  const inCart = has("template", t.slug);
  const cartItem = { kind: "template", id: t.slug, name: t.name, price: t.price, meta: `${t.type_label} · ${t.tier_label}` };
  const buyNow = () => { add(cartItem); nav("/cart"); };

  return (
    <div className="mx-auto max-w-6xl px-6 py-16" data-testid="template-detail">
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <Link to={`/templates/${t.slug}/preview`} className="relative group block" data-testid="detail-preview-frame">
          <TemplateThumb t={t} className="aspect-[16/11] rounded-3xl" />
          <div className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-5 h-11 font-semibold shadow-lg"><Eye className="h-5 w-5" /> Open Live Preview</span>
          </div>
        </Link>
        <div>
          <div className="flex items-center gap-2 text-sm">
            <span className="rounded-full bg-primary/10 text-primary px-3 py-1 font-semibold">{t.tier_label}</span>
            <span className="text-muted-foreground">{t.type_label} · {t.business_type} · {t.pages} pages</span>
          </div>
          <h1 className="font-head text-3xl md:text-4xl font-black mt-3">{t.name}</h1>
          <div className="flex items-center gap-1 mt-2 text-amber-400 text-sm">
            {[1,2,3,4,5].map(i => <Star key={i} className="h-4 w-4 fill-amber-400" />)}
            <span className="text-muted-foreground ml-1">Loved by {t.sales || 120}+ businesses</span>
          </div>
          <p className="text-muted-foreground mt-4">{t.description}</p>
          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-head text-4xl font-black text-primary">${t.price}</span>
            {t.original_price && t.original_price > t.price && (
              <span data-testid="detail-orig-price" className="text-xl text-muted-foreground line-through">${t.original_price}</span>
            )}
            {t.original_price && t.original_price > t.price && (
              <span className="text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 rounded-full px-3 py-1">Save ${(t.original_price - t.price).toFixed(2)}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to={`/templates/${t.slug}/preview`} data-testid="template-live-preview"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-7 h-12 font-semibold hover:opacity-90 transition-opacity">
              <Eye className="h-5 w-5" /> Live Preview
            </Link>
            <button data-testid="template-buy-now" onClick={buyNow}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 h-12 font-semibold hover:brightness-110 transition-[filter] shadow-lg shadow-primary/30">
              <Rocket className="h-5 w-5" /> Buy Now
            </button>
            <button data-testid="template-add-cart" onClick={() => add(cartItem)}
              className={`inline-flex items-center gap-2 rounded-full px-7 h-12 font-semibold transition-colors ${inCart ? "bg-emerald-500 text-white" : "border border-primary text-primary hover:bg-primary/10"}`}>
              {inCart ? <><Check className="h-5 w-5" /> In Cart</> : <><Plus className="h-5 w-5" /> Add to Cart</>}
            </button>
          </div>
          <div className="mt-4"><ShareButtons title={t.name} compact /></div>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2">
            {t.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}</li>)}
          </ul>
        </div>
      </div>

      <section className="mt-16 rounded-3xl glass p-8 md:p-12 grid-bg" data-testid="template-funnel">
        <h2 className="font-head text-2xl md:text-3xl font-extrabold text-center">From visitor to customer in 3 steps</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {funnelSteps.map((s) => (
            <div key={s.n} className="rounded-2xl bg-background/60 p-6 text-center">
              <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-head font-black mx-auto">{s.n}</div>
              <h3 className="font-head font-bold text-lg mt-3">{s.t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button onClick={buyNow} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 h-12 font-semibold hover:brightness-110 transition-[filter]"><Zap className="h-5 w-5" /> Get {t.name} — ${t.price}</button>
        </div>
      </section>

      <section className="mt-16" data-testid="template-services-upsell">
        <h2 className="font-head text-2xl md:text-3xl font-extrabold">Supercharge your new site</h2>
        <p className="text-muted-foreground mt-2">Bundle these with your site in one checkout and we'll drive real traffic, leads &amp; sales.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
          {[
            { id: "build", name: "Custom Website Build", price: "$299", unit: "one-time", to: "/get-started" },
            { id: "hosting", name: "Managed Secure Hosting", price: "$100", unit: "/year", to: "/get-started" },
            { id: "growth", name: "AI Growth Plan", price: "from $149", unit: "/mo", to: "/get-started?plan=gp_growth" },
            { id: "ads", name: "Ad Management", price: "from $300", unit: "/mo", to: "/get-started?ad=ads_rookie" },
          ].map((s) => (
            <div key={s.id} className="rounded-2xl glass p-5 flex flex-col">
              <h3 className="font-head font-bold">{s.name}</h3>
              <p className="text-primary font-head font-extrabold text-xl mt-1">{s.price}<span className="text-sm text-muted-foreground font-normal"> {s.unit}</span></p>
              <Link to={s.to} data-testid={`upsell-${s.id}`}
                className="mt-4 rounded-full h-10 text-sm font-semibold flex items-center justify-center gap-1.5 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                <Rocket className="h-4 w-4" /> Get started
              </Link>
            </div>
          ))}
        </div>
      </section>

      {data.upsell?.length > 0 && (
        <section className="mt-16" data-testid="template-related">
          <h2 className="font-head text-2xl md:text-3xl font-extrabold mb-6">You may also like</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.upsell.map((u) => <TemplateCard key={u.slug} t={u} />)}
          </div>
        </section>
      )}

      <div className="mt-14 text-center">
        <Link to="/templates" className="inline-flex items-center gap-1 text-primary font-semibold">Browse all templates <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}
